import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { isPlanRequired } from "../lib/analytics";
import { isFreeQuotaExceeded } from "../lib/upsell";
import { StandardPaywallDialog } from "../components/StandardUpsell";
import Layout from "../components/Layout";
import { RadarImportDialog } from "../components/RadarImportDialog";
import { RadarSettingsPanel } from "../components/RadarSettingsPanel";
import { useGeo, GeoSearchBar, MonitoredAreas, GeoOnboarding } from "../components/RadarGeo";
import RadarMap from "../components/RadarMap";
import RadarCoverage from "../components/RadarCoverage";
import ErrorBoundary from "../components/ErrorBoundary";
import OfferDeadlineBadge from "../components/OfferDeadline";
import {
    Broadcast as RadarIcon, ArrowsClockwise, MapPin, FileText,
    Trash, ArrowCounterClockwise, Lightning, CheckCircle, WarningCircle,
    CaretDown, CaretUp, Buildings, Trophy, Archive, FunnelX,
} from "@phosphor-icons/react";

const CATEGORIES = [
    { key: "", label: "Tutte" },
    { key: "priorita_alta", label: "Priorità alta" },
    { key: "interessante", label: "Interessante" },
    { key: "monitorare", label: "Monitorare" },
    { key: "scartare", label: "Scartare" },
];
const STATUSES = [
    { key: "", label: "Tutti" },
    { key: "screened", label: "Da valutare" },
    { key: "promoted", label: "Analizzate" },
    { key: "dismissed", label: "Scartate" },
    { key: "archived", label: "Archiviate" },
];

function categoryStyle(cat) {
    const map = {
        priorita_alta: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-300", label: "PRIORITÀ ALTA" },
        interessante: { bg: "bg-yellow-50", text: "text-yellow-800", border: "border-yellow-300", label: "INTERESSANTE" },
        monitorare: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-300", label: "MONITORARE" },
        scartare: { bg: "bg-red-50", text: "text-red-700", border: "border-red-300", label: "SCARTARE" },
    };
    return map[cat] || { bg: "bg-gray-100", text: "text-gray-500", border: "border-gray-300", label: (cat || "—").toUpperCase() };
}

function fmtEur(v) {
    if (v == null) return "—";
    return "€ " + Number(v).toLocaleString("it-IT", { maximumFractionDigits: 0 });
}

const DOC_KIND_LABEL = {
    perizia: "Perizia",
    avviso: "Avviso di vendita",
    ordinanza: "Ordinanza",
    other: "Documento",
};

function fmtPps(v) {
    if (v == null) return null;
    return Number(v).toLocaleString("it-IT", { maximumFractionDigits: 0 }) + " €/m²";
}

function MarketTag({ item, compact }) {
    const pos = item.market_position;
    const disc = item.market_discount_pct;
    if (!pos || pos === "sconosciuto" || disc == null) return null;
    let cls, text;
    if (pos === "sotto_mercato") {
        cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
        text = `−${Math.round(disc)}% vs mercato`;
    } else if (pos === "sopra_mercato") {
        cls = "bg-red-50 text-red-700 border-red-200";
        text = `+${Math.round(Math.abs(disc))}% vs mercato`;
    } else {
        cls = "bg-gray-50 text-gray-500 border-gray-200";
        text = "in linea";
    }
    return (
        <span
            data-testid={`market-tag-${item.id}`}
            className={`inline-flex items-center border ${cls} text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5`}
            title={item.market_avg_sqm ? `${fmtPps(item.price_per_sqm)} vs media ${fmtPps(item.market_avg_sqm)}` : undefined}
        >
            {compact ? `−${Math.round(disc)}%` : text}
        </span>
    );
}

export default function RadarDashboard() {
    const navigate = useNavigate();
    const { regions, provincesOf } = useGeo();
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [topToday, setTopToday] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [geo, setGeo] = useState({ region: "", province: "", comune: "" });
    const [radiusKm, setRadiusKm] = useState(0);
    const [centerCoords, setCenterCoords] = useState(null);
    const [geocoding, setGeocoding] = useState(false);
    const [filters, setFilters] = useState({ category: "", status: "screened", market: "" });
    const [expanded, setExpanded] = useState(null);
    const [promoting, setPromoting] = useState(null);
    const [paywall, setPaywall] = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [areasVersion, setAreasVersion] = useState(0);
    const [focusId, setFocusId] = useState(null);
    const [highlightId, setHighlightId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            const radiusActive = geo.comune && radiusKm > 0;
            if (radiusActive) {
                params.center_comune = geo.comune;
                if (geo.province) params.center_province = geo.province;
                params.radius_km = radiusKm;
            } else {
                if (geo.province) params.province = geo.province;
                else if (geo.region) params.region = geo.region;
                if (geo.comune) params.comune = geo.comune;
            }
            if (filters.category) params.category = filters.category;
            if (filters.status) params.status = filters.status;
            if (filters.market) params.market_position = filters.market;
            const [listRes, statsRes] = await Promise.all([
                api.get("/radar/auctions", { params }),
                api.get("/radar/stats"),
            ]);
            setItems(listRes.data);
            setStats(statsRes.data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [geo, radiusKm, filters]);

    const loadTopToday = useCallback(async () => {
        try {
            const { data } = await api.get("/radar/auctions", {
                params: { status: "screened", limit: 100 },
            });
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const todays = data.filter((d) => d.created_at && new Date(d.created_at) >= today);
            const pool = (todays.length ? todays : data)
                .sort((a, b) => (b.prescreening_score || 0) - (a.prescreening_score || 0));
            setTopToday(pool.slice(0, 4));
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadTopToday(); }, [loadTopToday]);

    // After an import, reset view so the new item is visible, then focus it.
    const handleImported = useCallback((radarId) => {
        loadTopToday();
        setGeo({ region: "", province: "", comune: "" });
        setRadiusKm(0);
        setFilters({ category: "", status: "screened", market: "" });
        if (radarId) setFocusId(radarId);
    }, [loadTopToday]);

    // Expand + scroll to + highlight the freshly imported item once it's in the list.
    useEffect(() => {
        if (!focusId || !items.some((i) => i.id === focusId)) return;
        setExpanded(focusId);
        setHighlightId(focusId);
        const el = document.querySelector(`[data-testid="radar-item-${focusId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        setFocusId(null);
        const t = setTimeout(() => setHighlightId(null), 2600);
        return () => clearTimeout(t);
    }, [items, focusId]);

    // Geocode the chosen comune to center the radius map
    useEffect(() => {
        let cancelled = false;
        if (!(geo.comune && radiusKm > 0)) { setCenterCoords(null); setGeocoding(false); return; }
        setGeocoding(true);
        api.get("/radar/geocode", { params: { comune: geo.comune, province: geo.province || undefined } })
            .then(({ data }) => {
                if (!cancelled) {
                    setCenterCoords(data.found ? { lat: data.lat, lng: data.lng, label: geo.comune } : null);
                }
            })
            .catch(() => { if (!cancelled) setCenterCoords(null); })
            .finally(() => { if (!cancelled) setGeocoding(false); });
        return () => { cancelled = true; };
    }, [geo.comune, geo.province, radiusKm]);

    // First-access onboarding: prompt for a geographic area if none monitored
    useEffect(() => {
        let cancelled = false;
        if (localStorage.getItem("radar_onboarded") === "1") return;
        api.get("/radar/areas").then(({ data }) => {
            if (!cancelled && (!data || data.length === 0)) setShowOnboarding(true);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const pollUntilDone = useCallback(async () => {
        let tries = 0;
        let backgroundCrawlNotified = false;
        const iv = setInterval(async () => {
            tries += 1;
            try {
                const { data } = await api.get("/radar/scan-status");
                // Inform the user that a slow portal crawl is running in
                // background — we never block on it but let them know fresh
                // items will appear automatically when it finishes.
                if (!backgroundCrawlNotified && data.crawl_in_progress && (data.crawl_provinces || []).length) {
                    backgroundCrawlNotified = true;
                    toast.info(
                        `Cerco aste fresche dai portali per ${data.crawl_provinces.join(", ")}… (può richiedere 2-5 minuti). Puoi continuare a usare l'app.`,
                        { duration: 8000 },
                    );
                }
                if (!data.is_running || tries > 120) {
                    clearInterval(iv);
                    setScanning(false);
                    if (data.last_result) {
                        toast.success(`Scansione completata · ${data.last_result.new} nuove · ${data.last_result.updated} aggiornate`);
                    }
                    await load();
                    await loadTopToday();
                    // Keep polling a few more times to catch the background
                    // crawl finish so the feed auto-refreshes when new items
                    // arrive after the user kicked the scan.
                    if (data.crawl_in_progress) {
                        let bgTries = 0;
                        const bgIv = setInterval(async () => {
                            bgTries += 1;
                            try {
                                const { data: bg } = await api.get("/radar/scan-status");
                                if (!bg.crawl_in_progress || bgTries > 120) {
                                    clearInterval(bgIv);
                                    if (bgTries <= 120) {
                                        toast.success("Crawler portali terminato — risultati aggiornati");
                                    }
                                    await load();
                                    await loadTopToday();
                                }
                            } catch {
                                clearInterval(bgIv);
                            }
                        }, 10000);
                    }
                }
            } catch {
                clearInterval(iv);
                setScanning(false);
            }
        }, 5000);
    }, [load, loadTopToday]);

    const afterAreasChanged = useCallback(() => {
        localStorage.setItem("radar_onboarded", "1");
        setAreasVersion((v) => v + 1);
        load();
        loadTopToday();
        // The backend kicks a background portal crawl for any new province —
        // start polling so the user sees fresh items appear automatically.
        pollUntilDone();
    }, [load, loadTopToday, pollUntilDone]);

    const runScan = async () => {
        setScanning(true);
        try {
            const { data } = await api.post("/radar/refresh");
            if (data.status === "already_running") {
                toast.info("Una scansione è già in corso…");
            } else {
                toast.info("Scansione fonti reali avviata… può richiedere alcuni minuti.");
            }
            pollUntilDone();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
            setScanning(false);
        }
    };

    const dismiss = async (id) => {
        try {
            await api.post(`/radar/auctions/${id}/dismiss`);
            toast.success("Asta scartata");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const restore = async (id) => {
        try {
            await api.post(`/radar/auctions/${id}/restore`);
            toast.success("Asta ripristinata");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const archive = async (id) => {
        try {
            await api.post(`/radar/auctions/${id}/archive`);
            toast.success("Asta archiviata");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const unarchive = async (id) => {
        try {
            const { data } = await api.post(`/radar/auctions/${id}/unarchive`);
            toast.success(`Asta ripristinata${data?.restored_to ? ` (${data.restored_to})` : ""}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const clearSearch = async () => {
        if (!window.confirm("Cancellare tutte le aree monitorate e i filtri attivi?")) return;
        try {
            await api.post("/radar/areas/clear");
            setGeo({ region: "", province: "", comune: "" });
            setRadiusKm(0);
            setFilters({ category: "", status: "screened", market: "" });
            setAreasVersion((v) => v + 1);
            localStorage.removeItem("radar_onboarded");
            toast.success("Ricerca resettata. Imposta una nuova area monitorata.");
            load();
            loadTopToday();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const promote = async (item) => {
        setPromoting(item.id);
        try {
            const { data } = await api.post(`/radar/auctions/${item.id}/promote`);
            toast.success("Analisi approfondita avviata");
            navigate(`/auctions/${data.auction_id}`);
        } catch (err) {
            if (isPlanRequired(err)) {
                setPaywall({ variant: "plan_required", source: "plan_required" });
            } else if (isFreeQuotaExceeded(err)) {
                setPaywall({ variant: "second_analysis_paywall", source: "second_analysis_paywall" });
            } else {
                toast.error(formatApiError(err.response?.data?.detail));
            }
            setPromoting(null);
        }
    };

    return (
        <Layout>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8" data-testid="radar-header">
                <div>
                    <div className="overline mb-2">Radar Aste · Italia</div>
                    <h1 className="font-display-black text-3xl sm:text-4xl lg:text-5xl leading-none flex items-center gap-3">
                        <RadarIcon size={36} weight="fill" className="shrink-0" />
                        Radar Aste reali.
                    </h1>
                    <p className="text-sm text-gray-600 mt-3 max-w-xl">
                        Aste residenziali reali trovate sui portali pubblici, ovunque in Italia,
                        deduplicate e ordinate per pre-screening score. Da ogni asta avvii l&apos;analisi completa a 8 agenti.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <RadarSettingsPanel onScan={runScan} onSaved={() => { load(); loadTopToday(); }} />
                    <RadarImportDialog onImported={handleImported} />
                    <button
                        onClick={clearSearch}
                        data-testid="radar-clear-search-btn"
                        title="Cancella aree monitorate e filtri, ricomincia da capo"
                        className="h-11 px-4 flex items-center gap-2 border border-gray-300 bg-white text-sm font-semibold hover:border-rose-400 hover:text-rose-700 transition-colors rounded-lg"
                    >
                        <FunnelX size={15} weight="bold" />
                        Cancella ricerca
                    </button>
                    <button
                        onClick={runScan}
                        disabled={scanning}
                        data-testid="radar-scan-now-btn"
                        className="h-11 px-5 grow sm:grow-0 justify-center flex items-center gap-2 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-60 transition-colors rounded-lg"
                    >
                        <ArrowsClockwise size={16} weight="bold" className={scanning ? "animate-spin" : ""} />
                        {scanning ? "Scansione…" : "Esegui scansione ora"}
                    </button>
                </div>
            </div>

            {/* Monitored areas (national, personal) */}
            <MonitoredAreas
                regions={regions}
                provincesOf={provincesOf}
                onChanged={afterAreasChanged}
                onPickArea={(a) => {
                    setGeo({ region: a.region, province: a.province, comune: a.comune || "" });
                    setFilters((f) => ({ ...f, status: "screened", category: "", market: "" }));
                }}
                key={areasVersion}
            />

            {/* Coverage dashboard (per region/province, sources, errors) */}
            <RadarCoverage version={areasVersion} />

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="radar-stats">
                    <StatCard label="Totale aste" value={stats.total} />
                    <StatCard label="Priorità alta" value={stats.by_category?.priorita_alta || 0} accent="emerald" />
                    <StatCard label="Interessanti" value={stats.by_category?.interessante || 0} accent="yellow" />
                    <StatCard label="Analizzate" value={stats.by_status?.promoted || 0} accent="blue" />
                    <StatCard label="Scartate" value={stats.by_status?.dismissed || 0} accent="red" />
                </div>
            )}

            {/* Top opportunities today */}
            {topToday.length > 0 && (
                <div className="mb-8" data-testid="radar-top-today">
                    <div className="flex items-center gap-2 mb-3">
                        <Trophy size={18} weight="fill" className="text-amber-500" />
                        <h2 className="text-lg font-bold">Top opportunità di oggi</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {topToday.map((it) => {
                            const cat = categoryStyle(it.prescreening_category);
                            return (
                                <button
                                    key={it.id}
                                    onClick={() => { setGeo({ region: "", province: "", comune: "" }); setFilters((f) => ({ ...f, category: "", status: "screened" })); setExpanded(it.id); }}
                                    data-testid={`radar-top-card-${it.id}`}
                                    className="text-left bg-white border border-gray-200 hover:border-black rounded-xl shadow-card overflow-hidden transition-colors group"
                                >
                                    {it.photos?.[0] ? (
                                        <div className="relative h-24 overflow-hidden">
                                            <img src={it.photos[0]} alt="" loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.classList.add("radar-photo-fallback"); }} />
                                            <div className="absolute inset-0 bg-gradient-to-t from-[#0D1322] to-transparent" />
                                        </div>
                                    ) : (
                                        <div className="relative h-24 overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#131B2E] to-[#0B111F]" data-testid={`radar-top-placeholder-${it.id}`}>
                                            <Buildings size={34} weight="duotone" className="text-slate-600" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-[#0D1322] to-transparent" />
                                        </div>
                                    )}
                                    <div className="p-4">
                                    <div className="flex items-start justify-between mb-2">
                                        <span className={`${cat.bg} ${cat.text} text-[10px] font-bold uppercase tracking-wider px-2 py-0.5`}>
                                            {cat.label}
                                        </span>
                                        <span className={`font-mono-tight font-bold text-2xl ${cat.text}`}>{it.prescreening_score}</span>
                                    </div>
                                    <div className="font-semibold text-sm text-gray-900 truncate">
                                        {(it.property_type || "Immobile").replace(/_/g, " ")}
                                        {it.municipality ? ` · ${it.municipality}` : ""}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-1 truncate">
                                        <MapPin size={11} /> {it.address || it.province || "—"}
                                    </div>
                                    <div className="text-xs text-gray-700 mt-2 font-mono-tight flex items-center justify-between gap-2">
                                        <span>{fmtEur(it.base_price)}</span>
                                        <MarketTag item={it} compact />
                                    </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* National search + filters */}
            <div className="mb-4">
                <GeoSearchBar
                    regions={regions}
                    provincesOf={provincesOf}
                    value={geo}
                    onChange={(v) => { setGeo(v); if (!v.comune) setRadiusKm(0); }}
                    onReset={() => { setGeo({ region: "", province: "", comune: "" }); setRadiusKm(0); }}
                    radiusKm={radiusKm}
                    onRadiusChange={setRadiusKm}
                />
            </div>
            {geo.comune && radiusKm > 0 && (
                centerCoords ? (
                    <ErrorBoundary
                        resetKey={`${centerCoords.lat},${centerCoords.lng},${radiusKm}`}
                        fallback={
                            <div
                                data-testid="radar-map-error"
                                className="border border-gray-200 bg-gray-50 mb-6 flex items-center justify-center text-sm text-gray-500"
                                style={{ height: 360 }}
                            >
                                Mappa non disponibile al momento.
                            </div>
                        }
                    >
                        <RadarMap center={centerCoords} radiusKm={radiusKm} items={items} />
                    </ErrorBoundary>
                ) : geocoding ? (
                    <div
                        data-testid="radar-map-skeleton"
                        className="border border-gray-200 bg-gray-100 mb-6 animate-pulse flex items-center justify-center text-sm text-gray-400"
                        style={{ height: 360 }}
                    >
                        Caricamento mappa…
                    </div>
                ) : null
            )}
            <div className="flex flex-wrap items-center gap-4 mb-6" data-testid="radar-filters">
                <FilterGroup
                    testid="radar-filter-category"
                    options={CATEGORIES}
                    value={filters.category}
                    onChange={(v) => setFilters((f) => ({ ...f, category: v }))}
                />
                <FilterGroup
                    testid="radar-filter-status"
                    options={STATUSES}
                    value={filters.status}
                    onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                />
                <button
                    data-testid="radar-filter-under-market"
                    onClick={() => setFilters((f) => ({ ...f, market: f.market === "sotto_mercato" ? "" : "sotto_mercato" }))}
                    className={`h-9 px-3 text-xs font-bold tracking-wider uppercase border transition-colors ${
                        filters.market === "sotto_mercato"
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    }`}
                >
                    Sotto-mercato
                </button>
            </div>

            {loading ? (
                <div className="text-sm text-gray-500 py-12 text-center">Caricamento…</div>
            ) : items.length === 0 ? (
                <EmptyRadar onScan={runScan} scanning={scanning} geo={geo} />
            ) : (
                <div className="space-y-3" data-testid="radar-list">
                    {items.map((it) => {
                        const cat = categoryStyle(it.prescreening_category);
                        const isOpen = expanded === it.id;
                        return (
                            <div
                                key={it.id}
                                data-testid={`radar-item-${it.id}`}
                                className={`bg-white border rounded-xl shadow-card overflow-hidden transition-all ${
                                    highlightId === it.id
                                        ? "border-emerald-500 ring-2 ring-emerald-400 ring-offset-2"
                                        : "border-gray-200 hover:border-gray-300"
                                }`}
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4">
                                <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                                    {it.photos?.[0] ? (
                                        <img
                                            src={it.photos[0]}
                                            alt=""
                                            loading="lazy"
                                            data-testid={`radar-photo-${it.id}`}
                                            className="hidden sm:block w-16 h-16 object-cover rounded-lg border border-white/10 shrink-0"
                                            onError={(e) => { e.currentTarget.src = ""; e.currentTarget.style.visibility = "hidden"; }}
                                        />
                                    ) : (
                                        <div
                                            data-testid={`radar-photo-placeholder-${it.id}`}
                                            className="hidden sm:flex w-16 h-16 items-center justify-center rounded-lg border border-white/10 bg-gradient-to-br from-[#131B2E] to-[#0B111F] shrink-0"
                                        >
                                            <Buildings size={22} weight="duotone" className="text-slate-600" />
                                        </div>
                                    )}
                                    {/* Score */}
                                    <div className={`flex flex-col items-center justify-center w-16 h-16 ${cat.bg} ${cat.border} border shrink-0`}>
                                        <span className={`font-mono-tight font-bold text-2xl ${cat.text}`}>
                                            {it.prescreening_score}
                                        </span>
                                        <span className="text-[9px] text-gray-500 uppercase">score</span>
                                    </div>
                                    {/* Main */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`${cat.bg} ${cat.text} text-[10px] font-bold uppercase tracking-wider px-2 py-0.5`}>
                                                {cat.label}
                                            </span>
                                            {it.is_demo && (
                                                <span className="bg-gray-100 text-gray-500 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">DEMO</span>
                                            )}
                                            <span className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">{it.source}</span>
                                            {it.distance_km != null && (
                                                <span
                                                    data-testid={`radar-distance-${it.id}`}
                                                    className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
                                                >
                                                    <MapPin size={10} weight="fill" /> {it.distance_km} km
                                                </span>
                                            )}
                                            <OfferDeadlineBadge
                                                auctionDateIso={it.auction_date_iso}
                                                auctionDate={it.auction_date}
                                                testid={`radar-deadline-${it.id}`}
                                            />
                                        </div>
                                        <div className="font-semibold text-gray-900 mt-1 truncate">
                                            {(it.property_type || "Immobile").replace(/_/g, " ")}
                                            {it.municipality ? ` · ${it.municipality}` : ""}
                                            {it.procedure_number ? ` · RGE ${it.procedure_number}` : ""}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                                            <span className="flex items-center gap-1">
                                                <MapPin size={11} />
                                                {it.address || it.province || "—"}
                                            </span>
                                            <span>Base {fmtEur(it.base_price)}</span>
                                            {it.surface_sqm && <span>{it.surface_sqm} m²</span>}
                                            {it.price_per_sqm != null && <span className="font-mono-tight">{fmtPps(it.price_per_sqm)}</span>}
                                            <MarketTag item={it} />
                                            {(it.documents?.length || 0) > 0 && (
                                                <span className="flex items-center gap-1">
                                                    <FileText size={11} />
                                                    {it.documents.length} doc
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    </div>
                                    {/* Actions */}
                                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                                        {it.status === "archived" ? (
                                            <button
                                                onClick={() => unarchive(it.id)}
                                                data-testid={`radar-unarchive-${it.id}`}
                                                className="h-10 px-4 flex items-center gap-2 border border-blue-300 bg-blue-50 text-blue-700 text-sm font-semibold hover:bg-blue-100 transition-colors"
                                            >
                                                <ArrowCounterClockwise size={15} weight="bold" />
                                                Ripristina
                                            </button>
                                        ) : it.status === "promoted" ? (
                                            <>
                                                <button
                                                    onClick={() => navigate(`/auctions/${it.promoted_auction_id}`)}
                                                    data-testid={`radar-view-analysis-${it.id}`}
                                                    className="h-10 px-4 flex items-center gap-2 border border-emerald-300 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 transition-colors"
                                                >
                                                    <CheckCircle size={15} weight="bold" />
                                                    Vedi analisi
                                                </button>
                                                <button
                                                    onClick={() => archive(it.id)}
                                                    data-testid={`radar-archive-${it.id}`}
                                                    className="h-10 w-10 flex items-center justify-center border border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-700 transition-colors"
                                                    title="Archivia"
                                                >
                                                    <Archive size={15} weight="bold" />
                                                </button>
                                            </>
                                        ) : it.status === "dismissed" ? (
                                            <>
                                                <button
                                                    onClick={() => restore(it.id)}
                                                    data-testid={`radar-restore-${it.id}`}
                                                    className="h-10 px-4 flex items-center gap-2 border border-gray-300 text-sm font-semibold hover:border-black transition-colors"
                                                >
                                                    <ArrowCounterClockwise size={15} weight="bold" />
                                                    Ripristina
                                                </button>
                                                <button
                                                    onClick={() => archive(it.id)}
                                                    data-testid={`radar-archive-${it.id}`}
                                                    className="h-10 w-10 flex items-center justify-center border border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-700 transition-colors"
                                                    title="Archivia"
                                                >
                                                    <Archive size={15} weight="bold" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => promote(it)}
                                                    disabled={promoting === it.id}
                                                    data-testid={`radar-promote-${it.id}`}
                                                    className="h-10 px-4 flex items-center gap-2 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-60 transition-colors"
                                                >
                                                    <Lightning size={15} weight="fill" />
                                                    {promoting === it.id ? "Avvio…" : "Analisi"}
                                                </button>
                                                <button
                                                    onClick={() => dismiss(it.id)}
                                                    data-testid={`radar-dismiss-${it.id}`}
                                                    className="h-10 w-10 flex items-center justify-center border border-gray-300 text-gray-500 hover:border-red-400 hover:text-red-600 transition-colors"
                                                    title="Scarta"
                                                >
                                                    <Trash size={15} weight="bold" />
                                                </button>
                                                <button
                                                    onClick={() => archive(it.id)}
                                                    data-testid={`radar-archive-${it.id}`}
                                                    className="h-10 w-10 flex items-center justify-center border border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-700 transition-colors"
                                                    title="Archivia"
                                                >
                                                    <Archive size={15} weight="bold" />
                                                </button>
                                            </>
                                        )}
                                        <button
                                            onClick={() => setExpanded(isOpen ? null : it.id)}
                                            data-testid={`radar-expand-${it.id}`}
                                            className="h-10 w-10 flex items-center justify-center border border-gray-300 hover:border-black transition-colors"
                                        >
                                            {isOpen ? <CaretUp size={15} weight="bold" /> : <CaretDown size={15} weight="bold" />}
                                        </button>
                                    </div>
                                </div>
                                {isOpen && (
                                    <div className="border-t border-gray-100 p-4 grid md:grid-cols-2 gap-6 bg-gray-50" data-testid={`radar-detail-${it.id}`}>
                                        <div>
                                            <div className="overline mb-2 flex items-center gap-1.5 text-emerald-700">
                                                <CheckCircle size={12} weight="fill" /> Motivi positivi
                                            </div>
                                            {(it.prescreening_reasons || []).length ? (
                                                <ul className="space-y-1">
                                                    {it.prescreening_reasons.map((r, i) => (
                                                        <li key={i} className="text-xs text-gray-700 flex gap-1.5">
                                                            <span className="text-emerald-500">+</span> {r}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : <p className="text-xs text-gray-400">Nessuno</p>}
                                        </div>
                                        <div>
                                            <div className="overline mb-2 flex items-center gap-1.5 text-orange-700">
                                                <WarningCircle size={12} weight="fill" /> Avvertenze
                                            </div>
                                            {(it.prescreening_warnings || []).length ? (
                                                <ul className="space-y-1">
                                                    {it.prescreening_warnings.map((w, i) => (
                                                        <li key={i} className="text-xs text-gray-700 flex gap-1.5">
                                                            <span className="text-orange-500">!</span> {w}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : <p className="text-xs text-gray-400">Nessuna</p>}
                                        </div>
                                        {(it.documents?.length || 0) > 0 && (
                                            <div className="md:col-span-2" data-testid={`radar-docs-${it.id}`}>
                                                <div className="overline mb-2 flex items-center gap-1.5">
                                                    <FileText size={12} weight="fill" /> Documenti dell&apos;asta
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {it.documents.map((d, i) => {
                                                        const label = DOC_KIND_LABEL[d.kind] || "Documento";
                                                        return d.url ? (
                                                            <a
                                                                key={i}
                                                                href={d.url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                data-testid={`radar-doc-link-${it.id}-${i}`}
                                                                className="inline-flex items-center gap-1.5 border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold hover:border-black transition-colors"
                                                            >
                                                                <FileText size={12} weight="bold" /> {label}
                                                            </a>
                                                        ) : (
                                                            <span
                                                                key={i}
                                                                className="inline-flex items-center gap-1.5 border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-500"
                                                            >
                                                                <FileText size={12} /> {label} (caricato)
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                                <p className="text-[11px] text-gray-400 mt-2">
                                                    Apri i documenti sul sito della fonte. Per l&apos;analisi AI della perizia,
                                                    se la fonte ne blocca il download automatico (robots.txt), scarica il PDF
                                                    e caricalo da &quot;Importa asta → Inserimento manuale&quot;.
                                                </p>
                                            </div>
                                        )}
                                        {it.source_url && (
                                            <div className="md:col-span-2">
                                                <a
                                                    href={it.source_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`radar-source-link-${it.id}`}
                                                    className="text-xs text-blue-600 hover:underline break-all"
                                                >
                                                    {it.source_url}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <GeoOnboarding
                regions={regions}
                provincesOf={provincesOf}
                open={showOnboarding}
                onClose={() => { localStorage.setItem("radar_onboarded", "1"); setShowOnboarding(false); }}
                onSaved={afterAreasChanged}
            />
            <StandardPaywallDialog open={!!paywall} onClose={() => setPaywall(null)}
                                   variant={paywall?.variant} source={paywall?.source} returnTo="/radar" />
        </Layout>
    );
}

function StatCard({ label, value, accent }) {
    const accentText = {
        emerald: "text-emerald-700", yellow: "text-yellow-800",
        blue: "text-blue-700", red: "text-red-700",
    }[accent] || "text-gray-900";
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-card p-4">
            <div className="overline mb-1">{label}</div>
            <div className={`font-mono-tight font-bold text-3xl ${accentText}`}>{value}</div>
        </div>
    );
}

function FilterGroup({ options, value, onChange, testid }) {
    return (
        <div className="flex gap-0 border border-gray-200 rounded-lg w-fit max-w-full overflow-x-auto" data-testid={testid}>
            {options.map((o) => (
                <button
                    key={o.key || "all"}
                    data-testid={`${testid}-${o.key || "all"}`}
                    onClick={() => onChange(o.key)}
                    className={`h-9 px-3 shrink-0 text-xs font-bold tracking-wider uppercase border-r last:border-r-0 border-gray-200 transition-colors first:rounded-l-lg last:rounded-r-lg ${
                        value === o.key ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function EmptyRadar({ onScan, scanning, geo }) {
    const searching = geo && (geo.region || geo.province || geo.comune);
    const areaLabel = geo?.comune || geo?.province || geo?.region;
    if (searching) {
        return (
            <div className="border border-gray-200 bg-white p-12 text-center" data-testid="radar-empty-state">
                <Buildings size={32} weight="bold" className="mb-4 text-gray-400 mx-auto" />
                <div className="overline mb-2">Copertura in espansione</div>
                <h2 className="font-display-black text-2xl mb-3">
                    {areaLabel ? `Nessuna asta in ${areaLabel}, per ora.` : "Nessuna asta in questa zona, per ora."}
                </h2>
                <p className="text-sm text-gray-600 mb-6 max-w-lg mx-auto">
                    Stiamo ampliando la copertura in questa zona. Continua a monitorarla e ti
                    mostreremo nuove opportunità non appena saranno disponibili.
                </p>
                <button
                    onClick={onScan}
                    disabled={scanning}
                    data-testid="radar-empty-scan-btn"
                    className="inline-flex h-11 px-5 items-center gap-2 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-60"
                >
                    <ArrowsClockwise size={16} weight="bold" className={scanning ? "animate-spin" : ""} />
                    {scanning ? "Ricerca in corso…" : "Cerca aste ora"}
                </button>
            </div>
        );
    }
    return (
        <div className="border border-gray-200 bg-white p-12 text-center" data-testid="radar-empty-state">
            <Buildings size={32} weight="bold" className="mb-4 text-gray-400 mx-auto" />
            <div className="overline mb-2">Radar vuoto</div>
            <h2 className="font-display-black text-2xl mb-3">Inizia a monitorare un&apos;area.</h2>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
                Aggiungi le tue aree (regione, provincia, comune) e il Radar inizierà a trovare
                aste reali per te. Oppure esegui subito una scansione.
            </p>
            <button
                onClick={onScan}
                disabled={scanning}
                data-testid="radar-empty-scan-btn"
                className="inline-flex h-11 px-5 items-center gap-2 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-60"
            >
                <ArrowsClockwise size={16} weight="bold" className={scanning ? "animate-spin" : ""} />
                {scanning ? "Scansione…" : "Esegui scansione ora"}
            </button>
        </div>
    );
}
